import { useBoolean } from '@chakra-ui/react';
import { InvoiceDiscount, InvoiceTaxRate, useInvoiceExpanded, useInvoiceSummaryV2 } from '@melio/ar-domain';
import { useAnalyticsContext, withAnalyticsContext } from '@melio/platform-analytics';
import { forwardRef, useDebouncedObject } from '@melio/platform-utils';
import { pick } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { ServiceChargeParams } from '../../types';
import { EditInvoiceExitModal } from './components';
import { EditInvoiceScreen } from './screens';
import { EditInvoiceSubmitTarget, InvoiceFormValuesLineItem } from './types';
import { useEditInvoiceActions, usePrefetch } from './utils';

export type EditInvoiceActivityProps = {
  invoiceId: string;
  onDone: (target?: EditInvoiceSubmitTarget) => unknown;
  onError?: ErrorFunction;
  taxRate?: InvoiceTaxRate;
  discount?: InvoiceDiscount;
  serviceCharges?: ServiceChargeParams[];
  onSetDiscount?: VoidFunction;
  onRemoveDiscount?: VoidFunction;
  onSetTaxRate?: VoidFunction;
  onAddServiceCharge?: VoidFunction;
  onRemoveServiceCharge?: (index: number) => void;
  onClose: (id?: string) => void;
};

export const EditInvoiceInnerActivity = forwardRef<EditInvoiceActivityProps>(
  (
    {
      invoiceId,
      discount,
      onClose,
      onRemoveDiscount,
      onSetDiscount,
      onSetTaxRate,
      onAddServiceCharge,
      onRemoveServiceCharge,
      onError,
      taxRate,
      serviceCharges,
      onDone,
      ...props
    },
    ref
  ) => {
    const { data: invoice, isLoading: isLoadingInvoice } = useInvoiceExpanded({ id: invoiceId, onError });
    const [lineItems, setLineItems] = useState<InvoiceFormValuesLineItem[]>(invoice?.invoiceLineItems ?? []);
    useEffect(() => invoice?.invoiceLineItems && setLineItems(invoice.invoiceLineItems), [invoice?.invoiceLineItems]);
    const lineItemsParams: InvoiceFormValuesLineItem[] =
      lineItems.length > 0 ? lineItems : invoice?.invoiceLineItems ?? [];

    const { data: summary, isFetching: isLoadingSummary } = useInvoiceSummaryV2({
      onError,
      params: useDebouncedObject(
        {
          discount: discount && pick(discount, 'type', 'value', 'name'),
          taxRate: taxRate ? pick(taxRate, 'rate', 'name') : undefined,
          lineItems: lineItemsParams
            .filter((li) => li.catalogItemId && li.quantity && li.price)
            .map((li) => pick(li, 'price', 'quantity', 'taxable')),
          serviceCharges: serviceCharges?.map((sc) => pick(sc, 'type', 'value', 'name')),
        },
        50
      ),
    });

    const [isExitDialogOpen, exitDialogOpen] = useBoolean(false);
    const [isDirty, setIsDirty] = useState(false);

    const { updateInvoice, isSaving } = useEditInvoiceActions({
      invoiceId,
      discount,
      serviceCharges,
      taxRate,
      onError,
    });

    const [submitTarget, setSubmitTarget] = useState<EditInvoiceSubmitTarget>();

    const { isLoading: isPreLoading } = usePrefetch();

    useAnalyticsContext({ globalProperties: { InvoiceId: invoice?.id } });

    return (
      <>
        <EditInvoiceExitModal
          isOpen={isExitDialogOpen}
          onApprove={() => {
            exitDialogOpen.off();
            onClose();
          }}
          onClose={exitDialogOpen.off}
        />
        <EditInvoiceScreen
          invoice={invoice}
          isLoading={isLoadingInvoice || isPreLoading}
          onClose={() => (isDirty ? exitDialogOpen.on() : onClose())}
          discount={{ id: discount?.id, onRemove: onRemoveDiscount, onEdit: onSetDiscount }}
          taxRate={{ id: taxRate?.id, onEdit: onSetTaxRate }}
          onSubmit={async (data, _, target) => {
            setSubmitTarget(target);
            await updateInvoice(data);
            onDone(target);
          }}
          defaultValues={useMemo(() => ({ ...invoice, lineItems: invoice?.invoiceLineItems ?? [] }), [invoice])}
          serviceCharges={{
            onAdd: onAddServiceCharge,
            onRemove: onRemoveServiceCharge,
          }}
          invoiceSummary={summary}
          onFormStateChange={({ isDirty }) => setIsDirty(isDirty)}
          onLineItemsChange={setLineItems}
          isLoadingSummary={isLoadingSummary}
          isSaving={isSaving ? submitTarget : undefined}
          {...props}
          ref={ref}
        />
      </>
    );
  }
);
export const EditInvoiceActivity = withAnalyticsContext(EditInvoiceInnerActivity);

EditInvoiceActivity.displayName = 'EditInvoiceActivity';
